import {
  ICON_TYPES,
  type IconType,
  Image,
  InformationSection,
  InformationSectionTextContent,
  Text,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { DialogContent, Stack } from "@mui/material";
import { FullScreenDialog } from "@src/appV2/lib/Dialogs";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { useRef } from "react";

import { useWorkplaceGuide, type WorkplaceGuideStep } from "./useWorkplaceGuide";

function getIcon(stepIcon: string): IconType {
  return ICON_TYPES.includes(stepIcon as IconType) ? (stepIcon as IconType) : "navigation-arrow";
}

function GuideStepCard({ step, stepNumber }: { step: WorkplaceGuideStep; stepNumber: number }) {
  return (
    <InformationSection iconType={getIcon(step.icon)} title={step.title} id={`step-${stepNumber}`}>
      <Image sx={{ "margin-top": -15 }} src={step.pictureUrl} height="200" alt={step.title} />
      <InformationSectionTextContent>{step.description}</InformationSectionTextContent>
    </InformationSection>
  );
}

interface WorkplaceClockInGuideModalProps {
  modalState: UseModalState;
  workplaceId: string;
}

export function WorkplaceClockInGuideModal(props: WorkplaceClockInGuideModalProps): JSX.Element {
  const { modalState, workplaceId } = props;
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { isLoading, data: guideResponse } = useWorkplaceGuide({
    workplaceId,
    category: "clock_in",
  });

  if (isLoading || !isDefined(guideResponse)) {
    return (
      <FullScreenDialog
        skipTopSafeAreaPadding
        modalState={modalState}
        variant="primary"
        sx={{ overflow: "hidden" }}
      >
        <StickyScrollPageHeader
          isLoading
          title="Timeclock Directions"
          scrollContainerRef={scrollContainerRef}
        />
        <DialogContent ref={scrollContainerRef} sx={{ padding: 7, paddingTop: 0 }}>
          <Text>Loading guide...</Text>
        </DialogContent>
      </FullScreenDialog>
    );
  }

  return (
    <FullScreenDialog
      skipTopSafeAreaPadding
      modalState={modalState}
      variant="primary"
      sx={{ overflow: "hidden" }}
    >
      <StickyScrollPageHeader
        title="Timeclock Directions"
        scrollContainerRef={scrollContainerRef}
        isLoading={false}
      />
      <DialogContent ref={scrollContainerRef} sx={{ padding: 7, paddingTop: 0 }}>
        <Stack spacing={10}>
          {(guideResponse.data.attributes.steps ?? []).map((step, index) => (
            <GuideStepCard key={`${step.title}-${step.icon}`} step={step} stepNumber={index + 1} />
          ))}
        </Stack>
      </DialogContent>
    </FullScreenDialog>
  );
}
